import { useCallback, useEffect, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type OtcCodeInputProps = {
  length?: number;
  onComplete: (code: string) => void;
  error?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  label?: string;
  errorMessage?: string;
};

export default function OtcCodeInput({
  length = 6,
  onComplete,
  error,
  disabled,
  autoFocus = true,
  label = "Verification code",
  errorMessage,
}: OtcCodeInputProps) {
  const [values, setValues] = useState<string[]>(Array(length).fill(""));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const errorId = useId();

  // Clear inputs when error is set
  useEffect(() => {
    if (error) {
      setValues(Array(length).fill(""));
      inputsRef.current[0]?.focus();
    }
  }, [error, length]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0]?.focus();
    }
  }, [autoFocus]);

  const submitCode = useCallback(
    (newValues: string[]) => {
      const code = newValues.join("");
      if (code.length === length) {
        onComplete(code);
      }
    },
    [length, onComplete],
  );

  const handleChange = (index: number, value: string) => {
    // Only allow single digits
    const digit = value.replace(/\D/g, "").slice(-1);
    if (!digit && value !== "") return;

    const newValues = [...values];
    newValues[index] = digit;
    setValues(newValues);

    if (digit && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    if (digit && index === length - 1) {
      submitCode(newValues);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      if (values[index]) {
        const newValues = [...values];
        newValues[index] = "";
        setValues(newValues);
      } else if (index > 0) {
        const newValues = [...values];
        newValues[index - 1] = "";
        setValues(newValues);
        inputsRef.current[index - 1]?.focus();
      }
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;

    const chars = pasted.slice(0, length).split("");
    const newValues = Array(length).fill("");
    chars.forEach((char, i) => {
      newValues[i] = char;
    });
    setValues(newValues);

    // Focus the next empty input, or the last one
    const nextEmpty = newValues.findIndex((v) => !v);
    const focusIndex = nextEmpty === -1 ? length - 1 : nextEmpty;
    inputsRef.current[focusIndex]?.focus();

    if (chars.length === length) {
      submitCode(newValues);
    }
  };

  return (
    <div
      role="group"
      aria-label={`${label}, ${length} digits`}
      aria-describedby={error && errorMessage ? errorId : undefined}
      className="flex justify-center gap-2"
      onPaste={handlePaste}
    >
      {values.map((val, i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label={`${label}, digit ${i + 1} of ${length}`}
          aria-invalid={error || undefined}
          maxLength={1}
          value={val}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          className={cn(
            "h-12 w-12 rounded-md border text-center font-mono text-lg outline-none transition-colors",
            "focus:border-blue-600 focus:ring-1 focus:ring-blue-600",
            error
              ? "border-red-500 text-red-600"
              : "border-gray-300 text-blue-900",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        />
      ))}
      {error && errorMessage && (
        <span id={errorId} role="alert" className="sr-only">
          {errorMessage}
        </span>
      )}
    </div>
  );
}
